import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from '../../store/store';
import { styles } from './cashTransactions.styles';
import { fetchCashBankTrnIds, fetchCashAuditedTrnIds, fetchCashTrnsByTrnId, setAuditorFilterWithCategory, changeCashInHand } from '../../store/slices/cashTransactionsSlice';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Alert,
    Snackbar,
    CircularProgress,
    TextField,
    MenuItem,
    Select,
    SelectChangeEvent,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface ChangeCashInHandResponse {
    message?: string;
}

const AuditorCashComponent: React.FC = () => {

    const [cashTrnDate, setCashTrnDate] = useState<Date>(new Date());
    const [bankTrnId, setBankTrnId] = useState<string>('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });
    const initializedRef = useRef(false);

    const dispatch = useDispatch<AppDispatch>();
    const cashInHandResponse = useSelector((state: RootState) => state.cashTransactions.transactions);
    const bankTrnIds = useSelector((state: RootState) => state.cashTransactions.cashBankTrnIds);
    const cashAuditedTrnIds = useSelector((state: RootState) => state.cashTransactions.cashAuditedTrnIds);
    const auditorFilterValueState = useSelector((state: RootState) => state.cashTransactions.auditorFilter);
    const auditorFilterCategory = useSelector((state: RootState) => state.cashTransactions.auditorFilterCategory);
    const { loading, error } = useSelector((state: RootState) => state.cashTransactions);

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    useEffect(() => {
        if (error) {
            showSnackbar(error, 'error');
        }
    }, [error]);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Update bank trn ID and date from first transaction in filtered list
    useEffect(() => {
        if (cashInHandResponse.length > 0) {
            const firstTrn = cashInHandResponse[0];
            if (firstTrn.cashBankTrnId) {
                setBankTrnId(firstTrn.cashBankTrnId);
            }
            if (firstTrn.cashTrnDate) {
                try {
                    setCashTrnDate(new Date(firstTrn.cashTrnDate));
                } catch (error) {
                    console.error('Invalid date format:', error);
                    showSnackbar('Invalid date format in transaction data', 'warning');
                }
            }
        } else {
            setBankTrnId('');
            setCashTrnDate(new Date());
        }
    }, [cashInHandResponse]);

    useEffect(() => {
        if (initializedRef.current) return;
        
        const fetchInitialData = async () => {
            try {
                const bankIds = await dispatch(fetchCashBankTrnIds('BANK')).unwrap();
                const auditedIds = await dispatch(fetchCashAuditedTrnIds('AUDITED')).unwrap();

                let shouldSetNewFilter = false;
                let newTrnId = '';
                let newCategory: 'BANK' | 'AUDITED' = 'BANK';

                // Check if current filter is still valid in its category list
                if (auditorFilterValueState) {
                    const isValidInCurrentCategory = 
                        (auditorFilterCategory === 'BANK' && bankIds.includes(auditorFilterValueState)) ||
                        (auditorFilterCategory === 'AUDITED' && auditedIds.includes(auditorFilterValueState));
                    
                    if (!isValidInCurrentCategory) {
                        shouldSetNewFilter = true;
                    }
                } else {
                    // No filter set yet
                    shouldSetNewFilter = true;
                }

                // Determine which filter to set (prioritize BANK first)
                if (shouldSetNewFilter) {
                    if (bankIds && bankIds.length > 0) {
                        newTrnId = bankIds[0];
                        newCategory = 'BANK';
                    } else if (auditedIds && auditedIds.length > 0) {
                        newTrnId = auditedIds[0];
                        newCategory = 'AUDITED';
                    }

                    if (newTrnId) {
                        dispatch(setAuditorFilterWithCategory({ trnId: newTrnId, category: newCategory }));
                        await dispatch(fetchCashTrnsByTrnId(newTrnId)).unwrap();
                    }
                } else {
                    // Current filter is valid, just fetch its data
                    await dispatch(fetchCashTrnsByTrnId(auditorFilterValueState)).unwrap();
                }
                
                initializedRef.current = true;
            } catch (error) {
                console.error('Error fetching transaction IDs:', error);
            }
        };

        fetchInitialData();
    }, [dispatch, auditorFilterValueState, auditorFilterCategory]);

    const handleFilterChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value; // Format: "BANK:TRNI23123" or "AUDITED:TRNI23123" 
        // Parse category and transaction ID
        const [category, trnId] = value.split(':') as ['BANK' | 'AUDITED', string];
        
        dispatch(setAuditorFilterWithCategory({ trnId, category }));
        dispatch(fetchCashTrnsByTrnId(trnId));
    };

    const handleSubmit = async () => {
        // Only allow auditing for BANK category
        if (auditorFilterCategory !== 'BANK' || cashInHandResponse.length === 0) {
            return;
        }

        // Get all transaction IDs from the filtered table
        const allTransactionIds = cashInHandResponse.map(trn => trn.id);

        const changeCashInHandPayload = {
            cashInHand: 'AUDITED',
            cashBankTrnId: cashInHandResponse[0].cashBankTrnId || '',
            cashTrnDate: cashInHandResponse[0].cashTrnDate,
            transactionIds: allTransactionIds,
        };

        try {
            const result = await dispatch(changeCashInHand(changeCashInHandPayload)).unwrap() as ChangeCashInHandResponse;

            const bankIds = await dispatch(fetchCashBankTrnIds('BANK')).unwrap();
            const auditedIds = await dispatch(fetchCashAuditedTrnIds('AUDITED')).unwrap();

            // Set filter to first available bank ID or audited ID (prioritize BANK)
            let nextFilterId = '';
            let nextCategory: 'BANK' | 'AUDITED' = 'BANK';
            
            if (bankIds && bankIds.length > 0) {
                nextFilterId = bankIds[0];
                nextCategory = 'BANK';
            } else if (auditedIds && auditedIds.length > 0) {
                nextFilterId = auditedIds[0];
                nextCategory = 'AUDITED';
            }

            if (nextFilterId) {
                dispatch(setAuditorFilterWithCategory({ trnId: nextFilterId, category: nextCategory }));
                await dispatch(fetchCashTrnsByTrnId(nextFilterId)).unwrap();
            }

            showSnackbar(result?.message || 'Transactions marked as AUDITED successfully!', 'success');
        }
        catch (error) {
            console.error('Failed to change cash in hand:', error);
            showSnackbar('Failed to mark transactions as AUDITED. Please try again.', 'error');
        }
    };

    const handleAuditedClick = () => {
        setConfirmDialogOpen(true);
    }

    const handleConfirmCancel = () => {
        setConfirmDialogOpen(false);
    }

    const handleConfirmProceed = async () => {
        setConfirmDialogOpen(false);
        await handleSubmit();
    }

    // Calculate total amount from all filtered transactions
    const totalAmount = cashInHandResponse.reduce((sum, transaction) => sum + transaction.amount, 0);

    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 240 }}>
                    <InputLabel
                        id="filter-select-label"
                        sx={{ color: '#ffffff' }}
                    >
                        Filter By
                    </InputLabel>
                    <Select
                        labelId="filter-select-label"
                        label="Filter By"
                        value={auditorFilterValueState ? `${auditorFilterCategory}:${auditorFilterValueState}` : ''}
                        onChange={handleFilterChange}
                        sx={styles.customSelect}
                        MenuProps={{
                            PaperProps: { sx: styles.selectMenuPaper },
                        }}
                    >
                        {bankTrnIds && bankTrnIds.length > 0 && bankTrnIds.map((trnId: any) => (
                            <MenuItem key={`BANK:${trnId}`} value={`BANK:${trnId}`}>
                                Bank Trn - {trnId}
                            </MenuItem>
                        ))}
                        {cashAuditedTrnIds && cashAuditedTrnIds.length > 0 && cashAuditedTrnIds.map((trnId: any) => (
                            <MenuItem key={`AUDITED:${trnId}`} value={`AUDITED:${trnId}`}>
                                Audited Trn - {trnId}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Box
                    component={Paper}
                    elevation={3}
                    sx={styles.formBox}
                >
                    <Typography sx={styles.totalAmountText}>Total Amount : <span style={{ color: "orange" }}>{totalAmount.toFixed(2)}</span></Typography>
                    <Box sx={styles.formFieldsBox}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Cash Trn Date"
                                value={cashTrnDate}
                                format="dd/MM/yyyy"
                                disabled={true}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setCashTrnDate(newValue)
                                    }
                                }}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: styles.dateTimePicker,
                                    },
                                }}
                            />
                        </LocalizationProvider>
                        <TextField
                            label="Bank Trn ID"
                            value={bankTrnId}
                            disabled={true}
                            onChange={(e) => setBankTrnId(e.target.value)}
                            size="small"
                            sx={styles.textField}
                        />

                       {initializedRef.current && auditorFilterCategory === 'BANK' && (
                        <Button
                            variant="contained"
                            onClick={handleAuditedClick}
                            disabled={cashInHandResponse.length === 0 || loading}
                            sx={styles.submitButton}
                        >
                            AUDITED
                        </Button>
                       )}
                    </Box>
                </Box>
            </Box>
            {/* Transactions Table */}
            <TableContainer component={Paper} sx={styles.tableContainer}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={styles.tableCellWithWidth('50px')}>Cash Trn Id</TableCell>
                            <TableCell sx={styles.tableCellWithWidth('100px')}>Trip Id</TableCell>
                            <TableCell sx={styles.tableCellWithWidth('50px')}>Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(!initializedRef.current || loading) ? (
                            <TableRow>
                                <TableCell colSpan={3} align="center">
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : (
                        cashInHandResponse.map((transaction: any) => (
                            <TableRow key={transaction.id}>
                                <TableCell sx={styles.tableCell}>{transaction.id}</TableCell>
                                <TableCell sx={styles.tableCell}>{transaction.tripCode}</TableCell>
                                <TableCell sx={styles.tableCell}>{transaction.amount.toFixed(2)}</TableCell>
                            </TableRow>
                        ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <Dialog
                open={confirmDialogOpen}
                onClose={handleConfirmCancel}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
                PaperProps={{
                    sx: {
                    backgroundColor: '#ffffff',
                    borderRadius: 2,
                    minWidth: 400,
                    padding: 2.5,
                    },
                }}
            >
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description" sx={{color:'tomato'}}>
                        This action will mark these transactions as AUDITED. <br />Are you sure you want to proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleConfirmCancel} variant="outlined" color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmProceed} variant="contained" color="primary" autoFocus>
                        Proceed
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >

                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>

            </Snackbar>
        </Box >
    )
}

export default AuditorCashComponent;